import { FC, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { useMutation } from '@apollo/client';
import { SalePoolLoanTableEventKind } from '__generated__/globalTypes';
import { StampOverlay, currentStampCount } from 'features/irm/StampOverlay';
import { SpinnerComponent } from 'ui-kit';

import {
  LogSalePoolLoanTableEvent,
  LogSalePoolLoanTableEventVariables,
} from 'mutation/__generated__/salePoolLoanTable';
import { LOG_SALE_POOL_LOAN_TABLE_EVENT } from 'mutation/salePoolLoanTable';

export const BLOCKED_EVENT_FLUSH_INTERVAL_MS = 2000;
export const PRINT_DISABLED_COPY = 'Printing is turned off for protected loan data.';

// One audit call per kind per interval, not per event: a held-down shortcut repeats copy events.
export function useBlockedEventLog(
  viewId: string | null,
): (kind: SalePoolLoanTableEventKind) => void {
  const [logEvent] = useMutation<LogSalePoolLoanTableEvent, LogSalePoolLoanTableEventVariables>(
    LOG_SALE_POOL_LOAN_TABLE_EVENT,
  );
  const pending = useRef(new Map<SalePoolLoanTableEventKind, number>());

  const flush = useCallback(() => {
    const counts = [...pending.current];
    pending.current.clear();
    if (!viewId) return;
    for (const [kind, count] of counts) {
      logEvent({ variables: { viewId, kind, count } }).catch(() => undefined);
    }
  }, [logEvent, viewId]);

  useEffect(() => {
    const timer = setInterval(flush, BLOCKED_EVENT_FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      flush();
    };
  }, [flush]);

  return useCallback(
    (kind: SalePoolLoanTableEventKind) => {
      if (!viewId) return;
      pending.current.set(kind, (pending.current.get(kind) ?? 0) + 1);
    },
    [viewId],
  );
}

export const ProtectedLoanArea: FC<{
  stamp: string;
  header: ReactNode;
  suspended?: boolean;
  onBlocked: (kind: SalePoolLoanTableEventKind) => void;
  children: ReactNode;
}> = ({ stamp, header, suspended = false, onBlocked, children }) => {
  const stampCount = useMemo(currentStampCount, []);
  const block = (kind: SalePoolLoanTableEventKind) => (event: SyntheticEvent) => {
    event.preventDefault();
    onBlocked(kind);
  };
  const inertWhenSuspended = suspended ? ({ inert: '' } as Record<string, string>) : {};

  return (
    <>
      <section
        data-testid="protected-loan-area"
        aria-label="Protected loan data"
        hidden={suspended}
        {...inertWhenSuspended}
        style={suspended ? { display: 'none' } : undefined}
        className="relative mt-4 select-none print:hidden"
        onCopy={block(SalePoolLoanTableEventKind.COPY)}
        onCut={block(SalePoolLoanTableEventKind.CUT)}
        onContextMenu={block(SalePoolLoanTableEventKind.CONTEXT_MENU)}
      >
        <div
          data-testid="loan-table-viewer-line"
          className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-300 light:text-gray-700"
        >
          {header}
        </div>
        <div className="relative overflow-hidden rounded border border-slate-500 light:border-gray-200">
          {children}
          <StampOverlay text={stamp} stampCount={stampCount} />
        </div>
      </section>
      {suspended && (
        <div
          role="status"
          className="mt-8 flex flex-col items-center justify-center gap-3 text-gray-200 light:text-gray-700"
        >
          <SpinnerComponent loading positionRelative />
          <p>Checking your access…</p>
        </div>
      )}
      <p className="hidden print:block">{PRINT_DISABLED_COPY}</p>
    </>
  );
};
